import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Building2, 
  Mail, 
  Phone, 
  Briefcase, 
  Star,
  Edit,
  Trash2,
  MessageSquare,
  Calendar
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ActivityTimeline } from "@/components/activities/ActivityTimeline";

interface ContactDetailSheetProps {
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
    position_title: string | null;
    is_primary_decision_maker: boolean | null;
    accounts: { name: string } | null;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onScheduleCall: () => void;
  onSendMessage: () => void;
}

export function ContactDetailSheet({
  contact,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onScheduleCall,
  onSendMessage,
}: ContactDetailSheetProps) {
  if (!contact) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary/10 text-primary text-xl">
                {contact.first_name[0]}{contact.last_name[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <SheetTitle className="text-2xl">
                  {contact.first_name} {contact.last_name}
                </SheetTitle>
                {contact.is_primary_decision_maker && (
                  <Star className="h-5 w-5 fill-warning text-warning" />
                )}
              </div>
              <SheetDescription>
                {contact.position_title || "No title specified"}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          <Button variant="outline" onClick={onScheduleCall} className="gap-2">
            <Calendar className="h-4 w-4" />
            Schedule Call
          </Button>
          <Button variant="outline" onClick={onSendMessage} className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Send Message
          </Button>
          <Button variant="outline" onClick={onEdit} className="gap-2">
            <Edit className="h-4 w-4" />
            Edit Contact
          </Button>
          <Button 
            variant="outline" 
            onClick={onDelete}
            className="gap-2 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>

        <Separator className="my-6" />

        {/* Contact Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">
              Contact Information
            </h3>
            <div className="space-y-3">
              {contact.email && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <a 
                      href={`mailto:${contact.email}`}
                      className="text-sm hover:text-primary transition-colors"
                    >
                      {contact.email}
                    </a>
                  </div>
                </div>
              )}
              
              {contact.phone && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <a 
                      href={`tel:${contact.phone}`}
                      className="text-sm hover:text-primary transition-colors"
                    >
                      {contact.phone}
                    </a>
                  </div>
                </div>
              )}

              {contact.accounts && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Account</p>
                    <p className="text-sm">{contact.accounts.name}</p>
                  </div>
                </div>
              )}

              {contact.position_title && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <Briefcase className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Position</p>
                    <p className="text-sm">{contact.position_title}</p>
                  </div>
                </div>
              )}

              {contact.is_primary_decision_maker && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
                  <Star className="h-5 w-5 fill-warning text-warning" />
                  <p className="text-sm text-warning-foreground">Primary Decision Maker</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Activity Timeline */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">
              Recent Activity
            </h3>
            <ActivityTimeline contactId={contact.id} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
